import { createStore, createHook, createContainer } from 'react-sweet-state';
import { ff } from '@atlassian/jira-feature-flagging';
import { isIssueSelected, isGroupSelected, diffIssuesSelected } from './selectors';
import type { IssueData, State } from './types';

const actions = {
	selectIssues:
		({
			issues,
			issuesData,
			selectGroup,
			rowIndex,
		}: {
			issues: string[];
			issuesData?: IssueData[]; // TODO: make issuesData required when removing use of issues https://jbusiness.atlassian.net/browse/HRZ-589
			selectGroup?: string;
			rowIndex?: number;
		}) =>
		({ setState, getState }: { setState: (arg: State) => void; getState: () => State }) => {
			const state = getState();

			const selectedIssuesDataMap = new Map(state.selectedIssuesData);
			if (ff('jwm.visual-hierarchy-dnd')) {
				issuesData?.forEach((issue) => {
					selectedIssuesDataMap.set(issue.issueId, issue);
				});
			}

			const newState = {
				...state,
				selectedIssues: new Set([...state.selectedIssues, ...issues]),
				...(ff('jwm.visual-hierarchy-dnd') &&
					issuesData && {
						selectedIssuesData: selectedIssuesDataMap,
					}),
				...(selectGroup ? { selectedGroups: new Set([...state.selectedGroups, selectGroup]) } : {}),
				...(rowIndex !== undefined ? { lastSelectedRowIndex: rowIndex } : {}),
			};
			setState(newState);
		},
	selectAllIssues:
		({
			issues,
			// TODO: make issuesData required when removing use of issues https://jbusiness.atlassian.net/browse/HRZ-589
			issuesData = [],
		}: {
			issues: string[];
			issuesData?: IssueData[];
		}) =>
		({ setState, getState }: { setState: (arg: State) => void; getState: () => State }) => {
			const state = getState();

			const selectedIssuesDataMap = new Map();
			if (ff('jwm.visual-hierarchy-dnd')) {
				issuesData?.forEach((issue) => {
					selectedIssuesDataMap.set(issue.issueId, issue);
				});
			}

			const newState = {
				...state,
				areAllIssuesSelected: true,
				selectedIssues: new Set(issues),
				...(ff('jwm.visual-hierarchy-dnd') && {
					selectedIssuesData: selectedIssuesDataMap,
				}),
				lastSelectedRowIndex: undefined,
			};
			setState(newState);
		},
	selectGroup:
		(groupId: string) =>
		({ setState, getState }: { setState: (arg: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				...state,
				selectedGroups: new Set([...state.selectedGroups, groupId]),
			});
		},
	selectAll:
		() =>
		({ setState, getState }: { setState: (arg: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				...state,
				areAllIssuesSelected: true,
			});
		},
	unselectAll:
		() =>
		({ setState, getState }: { setState: (arg: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				...state,
				areAllIssuesSelected: false,
				lastSelectedRowIndex: undefined,
			});
		},
	unselectGroup:
		(groupId: string) =>
		({ setState, getState }: { setState: (arg: State) => void; getState: () => State }) => {
			const { selectedGroups, ...state } = getState();
			selectedGroups.delete(groupId);
			setState({
				...state,
				areAllIssuesSelected: false,
				selectedGroups: new Set(selectedGroups),
			});
		},
	unselectIssues:
		({
			issues,
			unselectGroup,
			rowIndex,
		}: {
			issues: string[];
			unselectGroup?: string;
			rowIndex?: number;
		}) =>
		({ setState, getState }: { setState: (arg: State) => void; getState: () => State }) => {
			const { selectedIssues, selectedIssuesData, ...state } = getState();

			if (unselectGroup) {
				state.selectedGroups.delete(unselectGroup);
			}

			issues.forEach((issueId) => {
				selectedIssues.delete(issueId);
				if (ff('jwm.visual-hierarchy-dnd')) {
					selectedIssuesData.delete(issueId);
				}
			});

			const newState = {
				...state,
				areAllIssuesSelected: false,
				selectedIssues: new Set(selectedIssues),
				selectedIssuesData,
				...(unselectGroup ? { selectedGroups: new Set(state.selectedGroups) } : {}),
				...(rowIndex !== undefined ? { lastSelectedRowIndex: rowIndex } : {}),
			};
			setState(newState);
		},
	clearSelection:
		() =>
		({ setState, getState }: { setState: (arg: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				...state,
				areAllIssuesSelected: false,
				selectedGroups: new Set(),
				selectedIssues: new Set(),
				...(ff('jwm.visual-hierarchy-dnd') && { selectedIssuesData: new Map() }),
			});
		},
	toggleShowDeleteModal:
		({ showDeleteModal }: { showDeleteModal: boolean }) =>
		({ setState, getState }: { setState: (arg: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				...state,
				showDeleteModal,
			});
		},
	toggleBulkEditSidebar:
		({ showBulkEditSidebar }: { showBulkEditSidebar: boolean }) =>
		({ setState, getState }: { setState: (arg: State) => void; getState: () => State }) => {
			const state = getState();
			const { selectedIssues } = state;
			if (showBulkEditSidebar) {
				setState({
					...state,
					showBulkEditSidebar,
					bulkEditableIssues: new Set(selectedIssues),
				});
			} else {
				setState({
					...state,
					showBulkEditSidebar,
					bulkEditableIssues: new Set(),
				});
			}
		},
	setBulkChangePermission:
		({ bulkChangePermission }: { bulkChangePermission?: boolean }) =>
		({ setState, getState }: { setState: (arg: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				...state,
				bulkChangePermission,
			});
		},
	setFetchingGroups:
		(groups: string[]) =>
		({ setState, getState }: { setState: (arg: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				...state,
				fetchingGroups: groups,
			});
		},
	setLastSelectedRowIndex:
		(lastSelectedRowIndex: number) =>
		({ setState, getState }: { setState: (arg: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				...state,
				lastSelectedRowIndex,
			});
		},
};

export type Actions = typeof actions;
const initialState = {
	areAllIssuesSelected: false,
	selectedIssues: new Set<string>(),
	selectedIssuesData: new Map(),
	selectedGroups: new Set<string>(),
	showDeleteModal: false,
	showBulkEditSidebar: false,
	// groups that were collapsed and we are fetching the children to select them
	fetchingGroups: [],
	bulkEditableIssues: new Set<string>(),
};
const Store = createStore<State, Actions>({
	initialState,
	actions,
	name: 'JWMListBulkSelection',
});

export const BulkSelectionContainer = createContainer<State, Actions>(Store, {
	onInit:
		() =>
		({ setState }) => {
			setState({ ...initialState });
		},
});

export const useBulkSelectionStore = createHook(Store);

export const useIsIssueSelected = createHook(Store, {
	selector: isIssueSelected,
});

export const useIsGroupSelected = createHook(Store, {
	selector: isGroupSelected,
});

export const useDiffIssuesSelected = createHook(Store, {
	selector: diffIssuesSelected,
});
